/*
 * /auth/pending-patients (page)
 */
import {
	Button,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	PageHeader,
	PageWithEffect,
	SelectFormField,
	Form,
	toast,
} from "@emr/react-ui"
import type { AuthUser } from "@emr/types"
import { createFileRoute } from "@tanstack/react-router"
import { Clock, Loader2, UserCheck } from "lucide-react"
import { useState } from "react"
import { z } from "zod"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"
import { useAuth } from "@emr/auth/client"

export const Route = createFileRoute("/auth/pending-patients")({
	component: PendingPatientsPage,
	loader: rolePageLoader(["practitioner", "nurse", "admin", "super_admin"]),
})

const ROLE_OPTIONS = [
	{ label: "Patient", value: "patient" },
	{ label: "Practitioner", value: "practitioner" },
	{ label: "Nurse", value: "nurse" },
]

const approveSchema = z.object({
	role: z.enum(["patient", "practitioner", "nurse", "admin", "super_admin"]),
})
type ApproveValues = z.infer<typeof approveSchema>

function PendingPatientsPage() {
	const { role } = useAuth()
	const isAdmin = role === "admin" || role === "super_admin"

	const { data: users = [], isLoading, refetch } = trpc.user.getPendingPatients.useQuery()

	const [approveUser, setApproveUser] = useState<AuthUser | null>(null)

	return (
		<PageWithEffect effect="aurora" centerVertical={false}>
			<Container width="xxl" padding="lg" gap="lg">
				<PageHeader
					title="Pending Patients"
					right={{
						Component: (
							<Button variant="outline" size="sm" onClick={() => refetch()}>
								Refresh
								{users.length > 0 && (
									<span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700">
										{users.length}
									</span>
								)}
							</Button>
						),
					}}
				/>

				{isLoading && (
					<div className="flex items-center justify-center py-16">
						<Loader2 className="size-6 animate-spin text-muted-foreground" />
					</div>
				)}

				{!isLoading && users.length === 0 && (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<Clock className="size-10 text-muted-foreground/40" />
						<p className="text-sm text-muted-foreground">
							No pending patients — everyone has been reviewed.
						</p>
					</div>
				)}

				{!isLoading && users.length > 0 && (
					<div className="w-full overflow-x-auto rounded-lg border">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b bg-muted/40 text-left">
									<th className="px-4 py-3 font-medium">Name / Email</th>
									<th className="px-4 py-3 font-medium">Signed Up</th>
									{isAdmin && <th className="px-4 py-3 font-medium" />}
								</tr>
							</thead>
							<tbody>
								{users.map((u) => {
									const displayName =
										u.user_metadata?.displayName ?? u.email?.split("@")[0]
									const signedUp = new Date(u.created_at).toLocaleDateString(
										"en-CA",
										{ year: "numeric", month: "short", day: "numeric" },
									)
									return (
										<tr
											key={u.id}
											className="border-b last:border-0 hover:bg-muted/20"
										>
											<td className="px-4 py-3">
												<p className="font-medium text-foreground">
													{displayName}
												</p>
												<p className="text-xs text-muted-foreground">
													{u.email}
												</p>
											</td>
											<td className="px-4 py-3 text-muted-foreground">
												{signedUp}
											</td>
											{isAdmin && (
												<td className="px-4 py-3 text-right">
													<Button
														size="sm"
														onClick={() => setApproveUser(u as AuthUser)}
													>
														<UserCheck className="mr-1.5 size-4" />
														Approve
													</Button>
												</td>
											)}
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>
				)}
			</Container>

			{approveUser && (
				<ApproveDialog
					user={approveUser}
					open={!!approveUser}
					onOpenChange={(o) => !o && setApproveUser(null)}
					onSuccess={() => {
						refetch()
						setApproveUser(null)
					}}
				/>
			)}
		</PageWithEffect>
	)
}

function ApproveDialog({
	user,
	open,
	onOpenChange,
	onSuccess,
}: {
	user: AuthUser
	open: boolean
	onOpenChange: (o: boolean) => void
	onSuccess: () => void
}) {
	const approve = trpc.user.approveUser.useMutation({
		onSuccess: () => {
			toast.success("Patient approved")
			onSuccess()
		},
		onError: (e) => toast.error(e.message),
	})

	const displayName = user.user_metadata?.displayName ?? user.email

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-sm">
				<DialogHeader>
					<DialogTitle>Approve Patient</DialogTitle>
					<p className="text-sm text-muted-foreground">{displayName}</p>
				</DialogHeader>
				<Form<ApproveValues>
					schema={approveSchema}
					defaultValues={{ role: "patient" }}
					onSubmit={async (v) => {
						try {
							await approve.mutateAsync({ userId: user.id, role: v.role })
						} catch (e: any) {
							return e?.message ?? "An error occurred"
						}
					}}
					className="flex flex-col gap-4"
				>
					<SelectFormField
						name="role"
						label="Approve as"
						options={ROLE_OPTIONS}
						placeholder="Select role"
					/>
					<DialogFooter>
						<Button type="submit" disabled={approve.isPending}>
							{approve.isPending && (
								<Loader2 className="mr-2 size-4 animate-spin" />
							)}
							Approve
						</Button>
					</DialogFooter>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
